import { InformationIcon } from "@/assets/icons";
import { DatasetTypeIcon } from "@/components/dataset/datasetList/DatasetTypeIcon";
import MapMenuItemControl from "@/components/map/menuItemControl";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import Checkbox from "@/components/ui/checkbox";
import Tooltip from "@/components/ui/tooltip";
import {
  type Dataset,
  DatasetAttributeSchema,
  WeightValueEntry,
  WeightValues
} from "@/types/dataset";
import { ScoringOptions, ScoringOptionsEnum } from "@/types/spatial";
import { getNegativeImpactValue, getWeightString } from "@/utils/weightValues";

import { InputWithClear } from "./InputWithClear";

const WeightPercentage = ({ value = "0" }: { value?: string }) => {
  return (
    <div
      className="max-w-[60px] flex flex-row items-center justify-center h-fit bg-[#EEE] text-txtBody rounded-lg px-2 py-1 text-xs font-semibold border border-gainsboro"
      aria-label="weight-percentage"
    >
      {value}%
    </div>
  );
};

interface DatasetAttributeItemProps {
  dataset: Dataset;
  datasetAttributesWeights: Record<string, WeightValueEntry>;
  datasetWeightValue: string;
  datasetWeightPercentage: string;
  attributeGlobalPercentages: Record<string, string>;
  isAllAttributeWeightZero: boolean;
  isAllDatasetWeightZero: boolean;
  setLocalWeightValues: React.Dispatch<React.SetStateAction<WeightValues>>;
  selectedScoringOption: ScoringOptions | null;
}

const DatasetAttributeItem = ({
  dataset,
  datasetAttributesWeights,
  datasetWeightValue,
  datasetWeightPercentage,
  attributeGlobalPercentages,
  isAllAttributeWeightZero,
  isAllDatasetWeightZero,
  setLocalWeightValues,
  selectedScoringOption
}: DatasetAttributeItemProps) => {
  const datasetId = dataset.data.id;
  const isPoiDataset = dataset.properties.spatialType === "poi";

  const datasetCategories = dataset.categories.reduce<
    Record<string, DatasetAttributeSchema[][number]>
  >((acc, category) => {
    acc[category.id] = category;
    return acc;
  }, {});

  const handleUpdateDatasetWeightValue = (value: string, isPOI?: boolean) => {
    setLocalWeightValues((prevState) => {
      const attributeId = Object.keys(prevState[datasetId].attributes)[0];
      return {
        ...prevState,
        [datasetId]: {
          ...prevState[datasetId],
          weight: value,
          attributes: isPOI
            ? { [attributeId]: { weight: value } }
            : prevState[datasetId].attributes
        }
      };
    });
  };

  const updateLocalEntry = (
    attributeId: string,
    updater: (prev?: WeightValueEntry) => WeightValueEntry
  ) => {
    setLocalWeightValues((prevState) => {
      const prevDataset = prevState[datasetId] || {
        weight: "",
        attributes: {}
      };

      return {
        ...prevState,
        [datasetId]: {
          ...prevDataset,
          attributes: {
            ...prevDataset.attributes,
            [attributeId]: updater(prevDataset.attributes[attributeId])
          }
        }
      };
    });
  };

  const handleUpdateWeightValue = (categoryIdx: string, value: string) => {
    updateLocalEntry(categoryIdx, (prev) => ({
      weight: value,
      ...(typeof prev === "object" && prev?.negative_impact !== undefined
        ? { negative_impact: prev.negative_impact }
        : {})
    }));
  };

  const handleUpdateNegativeImpact = (
    attributeId: string,
    checked: boolean
  ) => {
    updateLocalEntry(attributeId, (prev) => {
      const entry: WeightValueEntry = {
        weight: typeof prev === "object" && prev ? prev.weight : "0"
      };

      if (checked) entry.negative_impact = true;

      return entry;
    });
  };

  const categoriesContent = Object.keys(datasetAttributesWeights).map((id) => {
    const attributeName =
      dataset.properties.spatialType === "poi"
        ? "Total POI Count" // For POI datasets, only show 1 attribute that can be assigned weight called "Total POI Count"
        : datasetCategories[id].attribute_name;

    return (
      <div key={id}>
        <div
          className="text-xs text-txtBody mb-2 line-clamp-2"
          title={attributeName}
        >
          {attributeName}
        </div>

        <div className="grid grid-cols-[1fr_60px] items-center gap-3">
          <InputWithClear
            inputClassName="text-xs"
            className="bg-white max-h-8 rounded-xl text-txtHeading"
            value={getWeightString(datasetAttributesWeights[id])}
            inputMode="decimal"
            dataTestId={`weight-input-${datasetId}-${id}`}
            onChange={(value) => handleUpdateWeightValue(id, value)}
            placeholder="Enter weight value"
            ariaLabel={`Weight value for ${attributeName}`}
            error={isAllAttributeWeightZero}
          />
          <WeightPercentage
            value={attributeGlobalPercentages[id]
              .toString()
              .replace(/\.00$/, "")}
          />
        </div>
        {selectedScoringOption === ScoringOptionsEnum.NORMALIZED && (
          <Checkbox
            inputClassName="size-4"
            className="mt-1.5 text-xs text-txtBody"
            label="Negative impact"
            onChange={(checked) => handleUpdateNegativeImpact(id, checked)}
            checked={Boolean(
              getNegativeImpactValue(datasetAttributesWeights[id])
            )}
          />
        )}
      </div>
    );
  });

  if (isPoiDataset) {
    return (
      <div className="flex flex-col w-full flex-1">
        <div className="flex items-center gap-1 px-4">
          <DatasetTypeIcon type={dataset.data.spatial_aggregation_type} />
          <Tooltip content={dataset.data.display_name} position="top-left">
            <span className="text-sm text-txtHeading font-semibold line-clamp-2 min-w-0 break-all">
              {dataset.data.display_name}
            </span>
          </Tooltip>
        </div>
        <div className="flex-1 grid grid-cols-[1fr_60px] w-full items-center gap-3 px-4 py-2">
          <InputWithClear
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            inputClassName="text-xs"
            className="bg-white max-h-8 rounded-xl text-txtHeading"
            value={datasetWeightValue?.toString()}
            onChange={(value) => handleUpdateDatasetWeightValue(value, true)}
            inputMode="decimal"
            error={isAllDatasetWeightZero}
          />
          <WeightPercentage
            value={datasetWeightPercentage?.toString().replace(/\.00$/, "")}
          />
        </div>
        {selectedScoringOption === ScoringOptionsEnum.NORMALIZED && (
          <Checkbox
            inputClassName="size-4"
            className="mt-1.5 text-xs text-txtBody px-4"
            label="Negative impact"
            onChange={(checked) =>
              handleUpdateNegativeImpact(
                Object.keys(datasetAttributesWeights)[0],
                checked
              )
            }
            checked={Boolean(
              getNegativeImpactValue(
                datasetAttributesWeights[
                Object.keys(datasetAttributesWeights)[0]
                ]
              )
            )}
          />
        )}
      </div>
    );
  }

  return (
    <AccordionItem
      value={dataset.data.id}
      className="flex flex-col w-full flex-1"
    >
      <div className="flex flex-row items-center px-4">
        <div className="flex items-center gap-1 mr-auto">
          <DatasetTypeIcon type={dataset.data.spatial_aggregation_type} />
          <Tooltip content={dataset.data.display_name} position="top-left">
            <span className="text-sm text-txtHeading font-semibold line-clamp-2 min-w-0 break-all">
              {dataset.data.display_name}
            </span>
          </Tooltip>
        </div>
        <MapMenuItemControl
          tooltipContent={
            <div>
              <p className="font-semibold">Negative Impact</p>
              <p>
                Set weight at dataset level to <br /> distribute equally across
                attributes
              </p>
            </div>
          }
          tooltipPosition="top-left"
          className="cursor-pointer w-5 h-5"
        >
          <InformationIcon className="fill-dim-gray" />
        </MapMenuItemControl>
      </div>

      <div className="flex flex-row items-center px-4">
        <div className="flex flex-col gap-3 w-full pt-2 mr-3">
          <div className="flex-1 grid grid-cols-[1fr_60px] w-full items-center gap-3">
            <InputWithClear
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              inputClassName="text-xs"
              className="bg-white max-h-8 rounded-xl text-txtHeading"
              value={datasetWeightValue?.toString()}
              onChange={(value) => handleUpdateDatasetWeightValue(value)}
              inputMode="decimal"
              error={isAllDatasetWeightZero}
            />
            <WeightPercentage
              value={datasetWeightPercentage?.toString().replace(/\.00$/, "")}
            />
          </div>
        </div>
        <AccordionTrigger className="w-auto px-0" />
      </div>

      <AccordionContent className="flex flex-col py-2 px-3  gap-2 mx-4 bg-white-smoke border border-whisper rounded-xl mt-2">
        {categoriesContent}
      </AccordionContent>
    </AccordionItem>
  );
};

export default DatasetAttributeItem;
